import {
	storeCurrentPage,
	storeMaxPrice,
	storeNextPageURL,
	storeSearchedText,
	storeSearchModal,
} from '$lib/js/stores'
import { page } from '$app/stores'
import { get } from 'svelte/store'
import { is_set_array, orderCategoryResults, setSearchParam } from '$lib/js/helpers'
import type { BaseProduct, FullProduct, LinkedProduct, ProductQueryOptions } from '$lib/js/elasticsearch/types'
import type { SiteScope } from '$lib/js/helpers/i18n'
import { fetchJSON } from '$lib/js/helpers/http'

export type ProductsEndpoint = 'brand' | 'category' | 'search'


/**
 * Get more products
 */
export async function getMoreProducts(options: ProductQueryOptions & {
	search_text?: string,
}, type: ProductsEndpoint, ctx: {scope: SiteScope}) {
	const filters = {
		type: type,

		start: options.start,
		// TODO(BF): clarify why we have options.sort AND sort
		sort: options.sort
			? orderCategoryResults(<string>options.sort)
			: orderCategoryResults(get(page).url.searchParams.get('sort') || 'rating'),
		min_price: options.min_price,
		max_price: options.max_price,
		brand_id: get(page).data.brand?.option_id || undefined,
		brand_ids: options.brand_ids || undefined,
		brand_slug: get(page).params.brand_url, // this is never used?
		cat_id: options.cat_id,
		cat_ids: options.cat_ids,
		search_text: options.search_text || get(storeSearchedText),
		promo_date: !!get(page).url.searchParams.get('promo'),
	}

	const payload = await fetchJSON<{
		products: (BaseProduct | FullProduct)[]
	}>(`/${ctx.scope}/endpoints/products`, {
		method: 'POST',
		json: filters,
	})

	if (!payload) {
		return []
	}

	return payload.products
}

export function getMoreBrandProducts(options: ProductQueryOptions, ctx: { scope: SiteScope }) {
	return getMoreProducts(options, 'brand', ctx)
}

export function getMoreCategoryProducts(options: ProductQueryOptions, ctx: { scope: SiteScope }) {
	return getMoreProducts(options, 'category', ctx)
}

export function getMoreSearchProducts(options: ProductQueryOptions, ctx: { scope: SiteScope }) {
	return getMoreProducts(options, 'search', ctx)
}

/**
 * Sort filtered products
 */
export async function orderFilterProducts(options: ProductQueryOptions, endpoint: ProductsEndpoint, ctx: {scope: SiteScope}) {
	if (!get(storeSearchModal)) {
		setSearchParam('p', '1')
	}
	options.start = 0

	if (get(page).route.id === '/[lang]/catalogsearch/result' || get(storeSearchModal)) {
		options.sort = 'default'
	} else if (!options.sort) {
		options.sort = 'rating'
	}

	return await getMoreProducts(options, endpoint, ctx)
}

/**
 * Set default current page
 */
export function setDefaultCurrentPage(options: ProductQueryOptions) {
	storeCurrentPage.set(1)
	get(page).url.searchParams.set('p', '1')
	storeNextPageURL.set(get(page).url.origin + get(page).url.pathname + '?p=2')

	/* Store max_price on first load of page */
	storeMaxPrice.set(parseInt((get(page).data?.max_price?.value * 1.21 + 1).toFixed(0)))
	options.max_price = get(storeMaxPrice)

	return options
}

/**
 * Update current page info
 */
export function updateCurrentPage(options: ProductQueryOptions) {
	/* 	storeCurrentPage.set(getIntSearchParam(get(page).url, 'p', 0))
	 */
	options.start = 50 * get(storeCurrentPage)
	storeNextPageURL.set(
		get(page).url.origin + get(page).url.pathname + '?p=' + (get(storeCurrentPage) + 1),
	)

	return options
}

/**
 * Update infinite scroll products
 */
export function loadMoreProducts<T>(products: T[], more_products: T[]): T[] {
	if (is_set_array(products) && is_set_array(more_products)) {
		products = products.concat(more_products)

		/* Update URL for pagination */
		storeCurrentPage.set(get(storeCurrentPage) + 1)
		get(page).url.searchParams.set('p', get(storeCurrentPage).toString())
		storeNextPageURL.set(
			get(page).url.origin + get(page).url.pathname + '?p=' + (get(storeCurrentPage) + 1),
		)
	}

	return products
}

/**
 * Get attribute options for 1 attribute products
 */
export function getAttributeStock(children: Array<any>, values: Array<any>, key: string) {
	for (const k in children) {
		let child_value = children[k][key]

		for (const z in values) {
			if (child_value === parseInt(values[z].value_index)) {
				values[z].stock = children[k].stock.source_quantity
			}
		}
	}

	return values
}

/**
 * Check product stock
 */
export function checkStock(product: FullProduct) {
	if (
		(product.type_id === 'simple' && product.stock.source_quantity === 0) ||
		(product.type_id === 'configurable' &&
			Array.isArray(product.configurable_options) &&
			product.configurable_options.length > 2)
	) {
		return 'no-stock'
	} else {
		return ''
	}
}


/**
 * Set correct related products prices
 */
export async function refreshRelatedProductPrices(related_products: LinkedProduct[], options: { scope: SiteScope }) {
	// TODO[perf]: fetch fewer fields; we only need the SKUs + pricing info
	const actual_related_products = await fetchJSON<(BaseProduct | null)[]>(`/${options.scope}/endpoints/sku_products`, {
		method: 'POST',
		json: related_products.map((prod) => prod.sku),
	})

	if (!actual_related_products) {
		return related_products
	}

	// build a {SKU -> actual_related_product} lookup for better performance
	const actual_related_products_lookup = actual_related_products.reduce(
		function(acc, actual_related_product) {
			if (actual_related_product) {
				acc[actual_related_product.sku] = actual_related_product
			}
			return acc
		},
		{} as Record<string, BaseProduct>,
	)

	// TODO: can't we just merge the actual product into the current one?
	return related_products.map((related_product) => {
		const actual_related_product = actual_related_products_lookup[related_product.sku]
		if (actual_related_product) {
			related_product.final_price = actual_related_product.final_price
			related_product.final_price_with_tax = actual_related_product.final_price_with_tax
			related_product.price = actual_related_product.price
			related_product.price_with_tax = actual_related_product.price_with_tax
			related_product.retail_price_es = actual_related_product.retail_price_es
			related_product.retail_price_fr = actual_related_product.retail_price_fr
			related_product.special_from_date = actual_related_product.special_from_date
			related_product.special_to_date = actual_related_product.special_to_date
			related_product.special_price = actual_related_product.special_price
			related_product.special_price_with_tax = actual_related_product.special_price_with_tax
			related_product.tax_rate = actual_related_product.tax_rate
		}
		return related_product
	})
}


/**
 * Get delivery date range
 */
function getDeliveryDate(orderDate: Date, delta: number) {
	let deliveryDate: Date,
		dayOfWeek: number

	const SAT = 6, SUN = 0, ONE_DAY = 60 * 60 * 24 * 1000,
		holidays = new Set([
			// fixed ones (same day every year)
			'1/1/2025', '6/1/2025', '1/5/2025', '15/8/2025', '12/10/2025',
			'1/11/2025', '6/12/2025', '8/12/2025', '25/12/2025',

			// moving ones
			// '17/4/2025',  // Jueves Santo
			'18/4/2025', // Viernes Santo
			'21/4/2025', // Lunes de Pascua
			'23/4/2025', // La Diada de Sant Jordi
			'9/6/2025', // Pentecostés
		])

	// Order on weekends gets shipped quickly on monday
	if (orderDate.getDay() === SAT) {
		delta -= 1
	}

	for (let i = 0; i <= delta; i++) {
		deliveryDate = new Date(+orderDate + ONE_DAY * i)
		dayOfWeek = deliveryDate.getDay()

		// Weekenddays does not count
		if (dayOfWeek === SAT
			|| dayOfWeek === SUN
			// Don't make this depend on the user locale otherwise it may not match dates in "holidays"
			|| holidays.has(deliveryDate.toLocaleDateString('es'))
		) {
			delta++
		}
	}
	return deliveryDate!
}

export function getDeliveryEstimatedDate({ start, end, type, scope }: {
	start?: number,
	end?: number,
	type?: 'long' | 'short',
	scope: SiteScope,
}) {
	if (!start || start < 2) {
		start = 2
	}

	end = end ?? start + 1
	type = type ?? 'long'

	/* Define constants */
	const today = new Date()
	// make this explicit so TypeScript is happy
	const day: 'numeric' = 'numeric'
	const OPTIONS = { month: type, day: day }

	/* Get start / finish dates */
	const startDate = getDeliveryDate(today, start).toLocaleDateString(scope, OPTIONS)
	const endDate = getDeliveryDate(today, end).toLocaleDateString(scope, OPTIONS) // End delta is one day later than start

	return {
		start: startDate,
		end: endDate,
	}
}
